import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { queryClient } from './lib/queryClient';
import { DryProvider } from './dry';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';
import ToastContainer from './components/common/ToastContainer';
import ErrorBoundary from './components/common/ErrorBoundary';
import VersionChecker from './components/common/VersionChecker';
import InstallPWA from './components/common/InstallPWA';
import OfflineIndicator from './components/common/OfflineIndicator';
import WebSocketQuerySync from './components/common/WebSocketQuerySync';
import Spinner from './components/common/Spinner';
import AnalyticsProvider from './components/common/AnalyticsProvider';
import { LockScreen } from './components/auth/LockScreen';
import { useOfflineUnlock } from './hooks/useOfflineUnlock';

// ============================================================================
// EAGER LOADS - Critical path pages loaded immediately
// ============================================================================

// Auth Pages - Must be fast for login/register flow
import Login from './pages/Login';
import Register from './pages/Register';

// Dashboard - First page after login, must be instant
import Dashboard from './pages/Dashboard';

// Public Pages - External user access, must be instant
import PublicAsset from './pages/PublicAsset';
import ReportIssue from './pages/ReportIssue';
import IssueConfirmation from './pages/IssueConfirmation';

// Core navigation pages - commonly accessed
import Assets from './pages/Assets';
import Forms from './pages/Forms';
import Submissions from './pages/Submissions';

// ============================================================================
// LAZY LOADS - Heavy or less frequently accessed pages
// ============================================================================

// Asset management (secondary pages)
const AssetForm = lazy(() => import('./pages/AssetForm'));
const AssetDetail = lazy(() => import('./pages/AssetDetail'));
const AssetTypes = lazy(() => import('./pages/AssetTypes'));

// Form builder - HEAVY (uses @dnd-kit for drag-and-drop)
const FormBuilder = lazy(() => import('./pages/FormBuilder'));

// Templates - Less frequently accessed
const Templates = lazy(() => import('./pages/Templates'));

// Submission detail - HEAVY (uses react-player, leaflet, lightbox, jspdf)
const SubmissionDetail = lazy(() => import('./pages/SubmissionDetail'));
const SubmissionForm = lazy(() => import('./pages/SubmissionForm'));

// Approvals - Less frequently accessed
const Approvals = lazy(() => import('./pages/Approvals'));

// Issues - Less frequently accessed
const Issues = lazy(() => import('./pages/Issues'));
const IssueDetail = lazy(() => import('./pages/IssueDetail'));

// Admin/Settings pages - HEAVY (various libs)
const Users = lazy(() => import('./pages/Users'));
const Analytics = lazy(() => import('./pages/Analytics')); // Uses recharts
const Settings = lazy(() => import('./pages/Settings'));
const Profile = lazy(() => import('./pages/Profile'));
const Billing = lazy(() => import('./pages/Billing'));
const Sites = lazy(() => import('./pages/Sites'));
const SiteDetail = lazy(() => import('./pages/SiteDetail'));
const Scan = lazy(() => import('./pages/Scan'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Jobs = lazy(() => import('./pages/Jobs'));
const JobSchedules = lazy(() => import('./pages/JobSchedules'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const JobExecute = lazy(() => import('./pages/JobExecute'));
const AdminPortal = lazy(() => import('./pages/AdminPortal'));
const SupportConsole = lazy(() => import('./pages/SupportConsole'));
const SupportTicketDetail = lazy(() => import('./pages/SupportTicketDetail'));
const MyTickets = lazy(() => import('./pages/MyTickets'));
const MyTicketDetail = lazy(() => import('./pages/MyTicketDetail'));
const MyPendingForms = lazy(() => import('./pages/MyPendingForms'));
const FormAssignments = lazy(() => import('./pages/FormAssignments'));
const Schedule = lazy(() => import('./pages/Schedule'));
const FlaggedSubmissions = lazy(() => import('./pages/FlaggedSubmissions'));
const CompliancePack = lazy(() => import('./pages/CompliancePack'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportBuilder = lazy(() => import('./pages/ReportBuilder'));
const ReportView = lazy(() => import('./pages/ReportView'));
const Setup = lazy(() => import('./pages/Setup'));
const PublicForm = lazy(() => import('./pages/PublicForm'));
const IssueRoutingSettings = lazy(() => import('./pages/IssueRoutingSettings'));
const QrCodes = lazy(() => import('./pages/QrCodes'));
const QrPrintCart = lazy(() => import('./pages/QrPrintCart'));
const QrBatches = lazy(() => import('./pages/QrBatches'));
const ScanHome = lazy(() => import('./pages/ScanHome'));

// ============================================================================
// LOADING FALLBACK - Shown while lazy components load
// ============================================================================

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Spinner size="lg" />
  </div>
);

// ============================================================================
// OFFLINE-UNLOCK GATE — four-state machine (see useOfflineUnlock)
// ============================================================================
// Sits inside DryProvider (so `http`/context exist) and wraps the whole router,
// in front of the protected app tree. For 'authed' and 'login' it defers to the
// existing routing (ProtectedRoute already distinguishes authenticated vs not via
// DryContext.user). For 'locked-online'/'locked-offline' it renders the LockScreen.
// Wrapping the full <Routes> means a mid-session 401 that clears the session and
// redirects to /login surfaces the LockScreen (state B) for an enrolled field
// worker rather than the login form. 'checking' never renders for a valid-token
// user (state is computed synchronously), so there is no flash.
function AuthGate({ children }: { children: React.ReactNode }) {
  // Hooks must run unconditionally (rules of hooks) before any early return.
  const location = useLocation();
  const { state, unlockBiometric, unlockPin, signInAgain } = useOfflineUnlock();

  // Public routes (anonymous form/asset/report access) must NEVER require unlock,
  // even on an enrolled device with an expired token. Bypass the gate entirely.
  // /q/ is the scan-landing route — auth-optional by design (QRResolver decides
  // what to show based on the code's target config, not the device lock state).
  if (location.pathname.startsWith('/public') || location.pathname.startsWith('/q/')) return <>{children}</>;

  if (state === 'checking') return <PageLoader />;

  if (state === 'locked-online' || state === 'locked-offline') {
    return (
      <LockScreen
        offline={state === 'locked-offline'}
        onUnlockBiometric={unlockBiometric}
        onUnlockPin={unlockPin}
        onSignInAgain={signInAgain}
      />
    );
  }

  // 'authed' and 'login' fall through to the existing routing.
  return <>{children}</>;
}

// reCAPTCHA Site Key (should be in .env for production)
const RECAPTCHA_SITE_KEY = import.meta.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key

function App() {
  return (
    <ErrorBoundary>
      <DryProvider>
      <QueryClientProvider client={queryClient}>
        <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
          <BrowserRouter>
            <AuthProvider>
              <WebSocketProvider>
              <AnalyticsProvider>
              <WebSocketQuerySync />
              <ToastContainer />
              <VersionChecker />
              <OfflineIndicator />
              <InstallPWA />
              <Suspense fallback={<PageLoader />}>
                <AuthGate>
                <Routes>
                  {/* Auth routes */}
                  <Route element={<AuthLayout />}>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                  </Route>

                  {/* Public routes (External User Access - no authentication required) */}
                  <Route path="/public/asset/:token" element={<PublicAsset />} />
                  <Route path="/public/report/:token" element={<ReportIssue />} />
                  <Route path="/public/confirm/:trackingNumber" element={<IssueConfirmation />} />
                  <Route path="/public/forms/:token" element={<PublicForm />} />

                  {/* QR/NFC resolver route — AUTH-OPTIONAL. The /q/{code} resolver
                      handles auth state itself (anonymous → public/login flows,
                      authed → form/assign). Must NOT sit behind a hard auth gate. */}
                  <Route path="/q/:code" element={<Scan />} />

                  {/* Legacy scan route - authenticated user QR/NFC scanning.
                      Kept working; Scan resolves :token via /q/{token} too. */}
                  <Route
                    path="/scan/:token"
                    element={
                      <ProtectedRoute>
                        <Scan />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected routes */}
                  <Route
                    element={
                      <ProtectedRoute>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/" element={<Dashboard />} />

                    {/* Setup hub — admin/manager landing for tenant configuration */}
                    <Route
                      path="/setup"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <Setup />
                        </ProtectedRoute>
                      }
                    />

                    {/* My Tasks (was: /my-forms) */}
                    <Route path="/my-forms" element={<MyPendingForms />} />
                    <Route path="/my-tasks" element={<MyPendingForms />} />

                    {/* Locations (was: /sites) — universal label across industries */}
                    <Route path="/sites" element={<Sites />} />
                    <Route path="/sites/:id" element={<SiteDetail />} />
                    <Route path="/locations" element={<Sites />} />
                    <Route path="/locations/:id" element={<SiteDetail />} />

                    {/* Scan home — manual code entry; any authed user */}
                    <Route path="/scan" element={<ScanHome />} />

                    {/* Assets */}
                    <Route path="/assets" element={<Assets />} />
                    <Route path="/assets/new" element={<AssetForm />} />
                    <Route path="/assets/:id" element={<AssetDetail />} />
                    <Route path="/assets/:id/edit" element={<AssetForm />} />

                    {/* Asset Types - Admin only */}
                    <Route
                      path="/asset-types"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin']}>
                          <AssetTypes />
                        </ProtectedRoute>
                      }
                    />

                    {/* Forms */}
                    <Route path="/forms" element={<Forms />} />
                    <Route path="/forms/new" element={<FormBuilder />} />
                    <Route path="/forms/:id/edit" element={<FormBuilder />} />

                    {/* Templates */}
                    <Route path="/templates" element={<Templates />} />

                    {/* Schedules (was: /form-assignments) — recurring + one-off check schedules */}
                    <Route
                      path="/form-assignments"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <FormAssignments />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/schedules"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <FormAssignments />
                        </ProtectedRoute>
                      }
                    />

                    {/* Calendar (was: /schedule) — unified view of all scheduled work */}
                    <Route path="/schedule" element={<Schedule />} />
                    <Route path="/calendar" element={<Schedule />} />

                    {/* Flagged submissions — manager triage queue */}
                    <Route
                      path="/flagged"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <FlaggedSubmissions />
                        </ProtectedRoute>
                      }
                    />

                    {/* Compliance pack — MD audit page */}
                    <Route
                      path="/compliance"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <CompliancePack />
                        </ProtectedRoute>
                      }
                    />

                    {/* Reports (Reporting C) — specific paths before :id */}
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <Reports />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports/new"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <ReportBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports/:id/edit"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <ReportBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports/:id"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <ReportView />
                        </ProtectedRoute>
                      }
                    />

                    {/* Submissions */}
                    <Route path="/submissions" element={<Submissions />} />
                    <Route path="/submissions/new" element={<SubmissionForm />} />
                    <Route path="/submissions/:id" element={<SubmissionDetail />} />

                    {/* Approvals */}
                    <Route path="/approvals" element={<Approvals />} />

                    {/* Issues */}
                    <Route path="/issues" element={<Issues />} />
                    <Route path="/issues/:id" element={<IssueDetail />} />

                    {/* Maintenance */}
                    <Route path="/maintenance" element={
                      <ErrorBoundary>
                        <Maintenance />
                      </ErrorBoundary>
                    } />

                    {/* Job Workflows */}
                    <Route path="/jobs" element={<Jobs />} />
                    <Route path="/jobs/new" element={<JobDetail />} />
                    <Route path="/jobs/execute/:id" element={<JobExecute />} />
                    <Route path="/jobs/:id" element={<JobDetail />} />
                    <Route
                      path="/job-schedules"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <JobSchedules />
                        </ProtectedRoute>
                      }
                    />

                    {/* Users - Admin only */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin']}>
                          <Users />
                        </ProtectedRoute>
                      }
                    />

                    {/* Analytics */}
                    <Route path="/analytics" element={<Analytics />} />

                    {/* Settings - Admin only */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin']}>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings/issue-routing"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin']}>
                          <IssueRoutingSettings />
                        </ProtectedRoute>
                      }
                    />

                    {/* Profile */}
                    <Route path="/profile" element={<Profile />} />

                    {/* Billing - Admin only */}
                    <Route
                      path="/billing"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin']}>
                          <Billing />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Portal - Super Admin only */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requiredRole={['super_admin']}>
                          <AdminPortal />
                        </ProtectedRoute>
                      }
                    />
                    {/* Support triage — dedicated route so support_agent reaches it
                        without the super-admin-only /admin console. */}
                    <Route
                      path="/admin/support"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'support_agent']}>
                          <SupportConsole />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/support/:id"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'support_agent']}>
                          <SupportTicketDetail />
                        </ProtectedRoute>
                      }
                    />
                    {/* QR / NFC label management — manager+ */}
                    <Route
                      path="/qr-codes/batches"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin']}>
                          <QrBatches />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/qr-codes/print-cart"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <QrPrintCart />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/qr-codes"
                      element={
                        <ProtectedRoute requiredRole={['super_admin', 'tenant_admin', 'manager']}>
                          <QrCodes />
                        </ProtectedRoute>
                      }
                    />

                    {/* Authenticated "My Tickets" — any logged-in user, matched by email */}
                    <Route path="/support/my-tickets" element={<ProtectedRoute><MyTickets /></ProtectedRoute>} />
                    <Route path="/support/my-tickets/:id" element={<ProtectedRoute><MyTicketDetail /></ProtectedRoute>} />
                  </Route>

                  {/* Fallback */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </AuthGate>
              </Suspense>
              </AnalyticsProvider>
            </WebSocketProvider>
          </AuthProvider>
          </BrowserRouter>
        </GoogleReCaptchaProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
      </DryProvider>
    </ErrorBoundary>
  );
}

export default App;
